import { SearchOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  message,
  Row,
  Select,
  Typography,
} from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import { find, get } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { IListColumn, ISortColumn, PaginationResponse } from 'src/@types/search-params';
import { getConnectionHistory } from 'src/api/connection';
import CGridView from 'src/components/CGridView';
import TableContent from 'src/components/CTable';
import { usePermission } from 'src/hooks/usePermission';
import useWindowSize from 'src/hooks/useWindowSize';
import { useAppSelector } from 'src/redux/hook';
import { setGlobalState } from 'src/redux/slice/globalSlice';
import { ConnectionType, historyStatus } from 'src/shared/enum/connection.enum';
import { IConnectionBatch } from 'src/shared/interface/connection';
import {
  exportExcel,
  formatPhoneNumberV2,
  nonAccentVietnamese,
  parseQueryString,
  renderSubType,
} from 'src/utils';
import { connectionTypeToString } from 'src/utils/connection';
import { SimType } from '../../../../shared/enum/sim.enum';
import { simTypeToText } from '../../../../utils/sim.enum';

const HistoryConnection = () => {
  const [form] = Form.useForm();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { openTabs, masterData, listPackageType, activeKeyId } = useAppSelector(
    (state) => state.global,
  );
  const merchantInfo: string | null = localStorage.getItem('merchantInfo');
  const [data, setData] = useState<PaginationResponse<IConnectionBatch>>();
  const [sortColumn] = useState<ISortColumn>();
  const [_, setSearchParams] = useSearchParams();
  const params = location.search;
  const size = useWindowSize();

  const { PERMISSIONS, hasPermission } = usePermission();

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    return current && current >= moment().endOf('day');
  };

  const handleFormSearch = (value: any) => {
    let values = { ...value };
    values = {
      ...values,
      search: values.search?.trim(),
      dateFrom:
        value.dateRange && value?.dateRange[0]
          ? value?.dateRange[0].format('YYYY-MM-DD')
          : undefined,
      dateTo:
        value.dateRange && value?.dateRange[1]
          ? value?.dateRange[1].format('YYYY-MM-DD')
          : undefined,
      dateRange: undefined,
    };
    Object.keys(values).forEach((key) => {
      if (!values[key]) {
        delete values[key];
      }
    });
    setSearchParams(values);
    fetchListConnectionHistory(values);
  };

  const fetchListConnectionHistory = async (params = {}) => {
    const res = await getConnectionHistory({
      ...params,
      DistributorId: JSON.parse(merchantInfo as string)?.merchantId,
    });
    if (res) {
      setData(res);
    }
  };

  const handleDetailOrder = (order: any) => {
    dispatch(
      setGlobalState({
        activeKeyMenu: `connection/history/list?id=${order.serial}&orderId=${order.orderId}`,
      }),
    );
    if (
      !openTabs.find(
        (tab) => tab.key === `connection/history/list?id=${order.serial}&orderId=${order.orderId}`,
      )
    ) {
      dispatch(
        setGlobalState({
          openTabs: [
            ...openTabs,
            {
              label: 'Xem lịch sử đấu nối ' + order.serial,
              children: '',
              key: `connection/history/list?id=${order.serial}&orderId=${order.orderId}`,
              closable: true,
            },
          ],
        }),
      );
    }
    navigate(`/management/connection/history/list?id=${order.serial}&orderId=${order.orderId}`);
  };

  const renderOptionSubType = () => {
    const listOption = [
      {
        value: '',
        label: 'Tất cả',
      },
    ];
    if (masterData?.subType && masterData?.subType?.length > 0) {
      masterData?.subType?.map((item: any) => {
        listOption.push({
          label: item?.name,
          value: item?.description,
        });
      });
    }
    return listOption;
  };

  useEffect(() => {
    fetchListConnectionHistory();
    form.resetFields();
  }, [activeKeyId]);

  const handleExport = () => {
    const link = document.createElement('a');
    link.target = '_blank';
    link.download = `lich_su_dau_noi_${moment().format('DDMMYYYYHHmmss')}.xlsx`;
    const url =
      process.env.REACT_APP_API_URL +
      '/api/Provider/history/export?DistributorId=' +
      JSON.parse(merchantInfo as string)?.merchantId +
      params.replace('?', '&');
    exportExcel(url)
      .then((res) => {
        link.href = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.ms-excel' }));
        link.click();
      })
      .catch((error) => {
        if (error.status === 403) {
          message.error(`Tài khoản của bạn không có quyền truy cập vào chức năng này`);
        }
      });
  };

  const columns: IListColumn[] = [
    { name: 'STT', fieldName: 'index', width: 20, isHideSort: true },
    {
      name: 'Mã lô',
      fieldName: 'orderId',
      renderSpecialCard: (record) => {
        return (
          (record?.type === 'UserProfileActiveLot' ||
            record?.type === 'ActiveLot' ||
            record?.type === 'ActiveLotLcs') && (
            <button
              disabled={!hasPermission(PERMISSIONS.CONNECTION.HISTORY_VIEW_DETAIL)}
              className={`underline md:whitespace-nowrap cursor-pointer bg-transparent border-transparent ${
                !hasPermission(PERMISSIONS.CONNECTION.HISTORY_VIEW_DETAIL)
                  ? 'text-gray-400'
                  : 'text-blue-400'
              } font-semibold text-right`}
              onClick={() => handleDetailOrder(record)}
            >
              {record.orderId ?? ''}
            </button>
          )
        );
      },
      isHideSort: true,
    },
    { name: 'Mã DN', fieldName: 'enterpriseId', isHideSort: true },
    { name: 'Tên DN', fieldName: 'enterpriseName', isHideSort: true },
    {
      name: 'Loại đấu nối',
      fieldName: 'connectionType',
      renderSpecialCard: (record) => {
        return (
          <span>{record.connectionType && connectionTypeToString(record.connectionType)}</span>
        );
      },
      isHideSort: true,
    },
    {
      name: 'Hình thức',
      fieldName: 'simMode',
      isHideSort: true,

      renderSpecialCard: (record) => {
        return (
          <span>
            {find(get(masterData, 'simMode', []), (opt) => opt.code === record?.simMode)?.name}
          </span>
        );
      },
    },
    {
      name: 'Loại SIM',
      fieldName: 'simType',
      isHideSort: true,
      renderSpecialCard: (record) => {
        return <Typography.Text>{simTypeToText(record?.simType)}</Typography.Text>;
      },
    },
    {
      name: 'Loại gói',
      fieldName: 'packageType',
      isHideSort: true,
      renderSpecialCard: (record) => {
        return (
          <Typography.Text>
            {listPackageType?.find((item) => item.description === record?.packageType)?.name ??
              'Gói thường'}
          </Typography.Text>
        );
      },
    },
    {
      name: 'Loại thuê bao',
      fieldName: 'subType',
      isHideSort: true,
      renderSpecialCard: (record) => {
        return renderSubType(masterData, record?.subType);
      },
    },
    {
      name: 'SIM serial',
      fieldName: 'serial',
      isHideSort: true,
      renderSpecialCard: (record) => {
        return record.serial ?? '';
      },
    },
    {
      name: 'Mã gói',
      fieldName: 'mbfCode',
      isHideSort: true,
    },
    {
      name: 'Tên gói',
      fieldName: 'mbfName',
      isHideSort: true,
    },
    {
      name: 'Số điện thoại',
      fieldName: 'phoneNumber',
      isHideSort: true,
      renderSpecialCard: (record) => {
        return <span>{formatPhoneNumberV2(record?.phoneNumber)}</span>;
      },
    },
    { name: 'Trạng thái', fieldName: 'statusString', isHideSort: true },
    { name: 'Mã lỗi', fieldName: 'errorCode', isHideSort: true },
    { name: 'Mô tả lỗi', fieldName: 'errorMessage', isHideSort: true },
    { name: 'Nguời tạo', fieldName: 'createdBy', isHideSort: true },
    {
      name: 'Ngày tạo',
      fieldName: 'createdAt',
      renderSpecialCard: (record) => {
        return (
          <>
            <p>
              <p>{record.createdAt}</p>
            </p>
          </>
        );
      },
    },
    { name: 'Nguời cập nhật', fieldName: 'updatedBy' },
    {
      name: 'Ngày cập nhật',
      fieldName: 'updatedAt',
      renderSpecialCard: (record) => {
        return <p>{record?.updatedAt}</p>;
      },
    },
    {
      name: 'Thao tác',
      fieldName: 'id',
      renderSpecialCard: (order) => {
        return (
          <button
            disabled={!hasPermission(PERMISSIONS.CONNECTION.HISTORY_VIEW_DETAIL)}
            className={`underline whitespace-nowrap cursor-pointer bg-transparent border-transparent ${
              !hasPermission(PERMISSIONS.CONNECTION.HISTORY_VIEW_DETAIL)
                ? 'text-gray-400'
                : 'text-blue-400'
            } font-semibold`}
            onClick={() => handleDetailOrder(order)}
          >
            Chi tiết
          </button>
        );
      },
      isHideSort: true,
    },
  ];

  return (
    <div>
      <Form
        onFinish={handleFormSearch}
        layout={size.width < 768 ? 'vertical' : 'inline'}
        className='gap-2'
      >
        <Form.Item name='search'>
          <Input
            prefix={<SearchOutlined />}
            placeholder='Mã lô, Sim Serial, Số điện thoại, Mã DN'
            className='w-full md:w-[400px]'
          />
        </Form.Item>
        <Form.Item name='simMode' label='Hình thức SIM'>
          <Select
            showSearch
            className='min-w-[200px]'
            placeholder='Vui lòng chọn'
            defaultValue={''}
            options={[
              { value: '', label: 'Tất cả' },

              ...get(masterData, 'simMode', []).map((item: any) => ({
                label: item.name,
                value: item.code,
              })),
            ]}
          ></Select>
        </Form.Item>
        <Form.Item name='simType' label='Loại SIM'>
          <Select
            showSearch
            className='min-w-[150px]'
            placeholder='Vui lòng chọn'
            options={[
              {
                value: null,
                label: 'Tất cả',
              },
              {
                value: SimType.PHYSICAL_SIM,
                label: simTypeToText(SimType.PHYSICAL_SIM),
              },
              {
                value: SimType.ESIM,
                label: simTypeToText(SimType.ESIM),
              },
            ]}
          ></Select>
        </Form.Item>{' '}
        <Form.Item name='PackageType' label='Loại gói'>
          <Select
            showSearch
            className='min-w-[150px]'
            placeholder='Vui lòng chọn'
            defaultValue={''}
            options={[
              {
                value: '',
                label: 'Tất cả',
              },
              ...listPackageType.map((item) => ({ label: item.name, value: item.description })),
            ]}
            filterOption={(input, option) => {
              return nonAccentVietnamese(
                (option!.label as unknown as string).toString()?.toLowerCase(),
              ).includes(nonAccentVietnamese(input?.toLowerCase()?.trim()));
            }}
          ></Select>
        </Form.Item>
        <Form.Item name='type' label='Loại đấu nối'>
          <Select
            showSearch
            className='min-w-[150px]'
            placeholder='Vui lòng chọn'
            options={[
              {
                value: null,
                label: 'Tất cả',
              },
              {
                value: ConnectionType.ENTERPRISE,
                label: connectionTypeToString(ConnectionType.ENTERPRISE),
              },
              {
                value: ConnectionType.INDIVIDUAL,
                label: connectionTypeToString(ConnectionType.INDIVIDUAL),
              },
            ]}
          ></Select>
        </Form.Item>
        <Form.Item name='subType' label='Loại thuê bao'>
          <Select
            showSearch
            className='min-w-[200px]'
            placeholder='Vui lòng chọn'
            options={renderOptionSubType()}
            filterOption={(input, option) => {
              return nonAccentVietnamese(
                (option!.label as unknown as string).toString()?.toLowerCase(),
              ).includes(nonAccentVietnamese(input?.toLowerCase()?.trim()));
            }}
          ></Select>
        </Form.Item>
        <Form.Item name='StatusString' label='Trạng thái'>
          <Select
            showSearch
            className='min-w-[150px]'
            placeholder='Vui lòng chọn'
            options={historyStatus}
          ></Select>
        </Form.Item>
        <Form.Item name='dateRange' label='Ngày tạo'>
          <DatePicker.RangePicker
            disabledDate={disabledDate}
            placeholder={['Từ ngày', 'đến ngày']}
            format={['DD/MM/YYYY', 'DD/MM/YYYY']}
          />
        </Form.Item>
        <div className='row-button' style={{ justifyContent: 'flex-end', margin: '1.5rem 0' }}>
          <Button type='primary' htmlType='submit'>
            Tìm kiếm
          </Button>
          {hasPermission(PERMISSIONS.CONNECTION.HISTORY_EXPORT) && (
            <Button onClick={handleExport}>Export</Button>
          )}
        </div>
      </Form>
      <div className='mb-10 md:mb-0 mt-6'>
        {size.width < 1024 ? (
          <CGridView
            length={data?.results.length}
            totalRecord={data?.rowCount ?? 0}
            columns={columns}
            title={() => <div className='text-xl font-bold mb-6'>Danh sách lịch sử đấu nối</div>}
          >
            {data?.results && data?.results.length > 0 ? (
              data?.results.map((item: IConnectionBatch) => {
                return (
                  <div key={'data-row-' + item.id}>
                    <Divider className='border-t-black' />

                    <Row className='mb-6 items-center' gutter={[4, 16]}>
                      {columns.map((ele) => {
                        if (ele.fieldName !== 'index' && ele.fieldName !== 'action') {
                          return (
                            <>
                              <Col span={11} className={`font-bold label`}>
                                <div className='flex gap-2 items-center'>{ele.name}</div>
                              </Col>
                              <Col className='break-words text-right' span={13}>
                                {ele?.renderSpecialCard
                                  ? ele?.renderSpecialCard(item, null, null, null)
                                  : item[`${ele?.fieldName}`]}
                              </Col>
                            </>
                          );
                        } else if (ele.fieldName === 'action') {
                          return (
                            <Col key={'column-' + item.id + ele.fieldName} span={24}>
                              <div className='flex gap-6'>
                                {ele?.renderSpecialCard
                                  ? ele?.renderSpecialCard(item, null, null, null)
                                  : item[`${ele?.fieldName}`]}
                              </div>
                            </Col>
                          );
                        } else {
                          return <></>;
                        }
                      })}
                    </Row>
                  </div>
                );
              })
            ) : (
              <p>{'Không có dữ liệu'}</p>
            )}
          </CGridView>
        ) : (
          <TableContent
            bordered
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'Không tìm thấy kết quả' }}
            dataSource={data?.results ?? []}
            columns={columns}
            totalRecord={data?.rowCount ?? 0}
            sortColumn={sortColumn}
            onPageChange={(pageNumber, pageSize) => {
              const pagination = parseQueryString(location.search);
              fetchListConnectionHistory({
                ...pagination,
                pageIndex: pageNumber ?? 1,
                pageSize: pageSize ?? 10,
                sortField: pagination.sortField ?? undefined,
                asc: pagination.asc ?? undefined,
              });
            }}
            onSort={(sort: ISortColumn) => {
              const pagination = parseQueryString(location.search);
              fetchListConnectionHistory({
                ...pagination,
                pageIndex: pagination.pageIndex ?? 1,
                pageSize: pagination.pageSize ?? 10,
                sortField: sort.fieldName,
                asc:
                  sort.orderType === 'asc' ? true : sort.orderType === 'desc' ? 'false' : undefined,
              });
            }}
          />
        )}
      </div>
    </div>
  );
};

export default HistoryConnection;
